import { useState, useEffect, useRef, useMemo } from 'react'
import { supabase } from '../../api/supabase'

export interface ColDef {
  readonly id: string
  readonly label: string
}

export interface MigrateOptions {
  /** Old key inside user_preferences.preferences JSONB (same {order,hidden} shape) */
  dbKey?: string
  /** Old localStorage key for hidden[] array */
  lsHidden?: string
  /** Old localStorage key for order[] array */
  lsOrder?: string
}

// Spreads ...existing so any untracked fields (e.g. sortBy) survive order/hidden-only saves.
async function mergeAndSavePrefs(
  userId: string,
  prefKey: string,
  order: string[],
  hidden: string[],
  extra?: Record<string, unknown>
) {
  const { data, error: selectError } = await supabase
    .from('user_preferences')
    .select('preferences')
    .eq('user_id', userId)
    .maybeSingle()
  // Z71.2: this used to fail silently on a permission-denied error (missing GRANT on
  // user_preferences -- fixed in 20260909c) -- a column toggle would just quietly not persist,
  // with nothing in the console to explain why. Surface it loudly instead.
  if (selectError) console.error(`[useColumnPrefs] Failed to load prefs for "${prefKey}":`, selectError)
  const allPrefs = ((data?.preferences ?? {}) as Record<string, unknown>)
  const existing = (allPrefs[prefKey] as Record<string, unknown>) ?? {}
  const { error: upsertError } = await supabase
    .from('user_preferences')
    .upsert(
      {
        user_id: userId,
        preferences: { ...allPrefs, [prefKey]: { ...existing, order, hidden, ...(extra ?? {}) } },
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'user_id' }
    )
  if (upsertError) console.error(`[useColumnPrefs] Failed to save prefs for "${prefKey}":`, upsertError)
}

export function useColumnPrefs(
  prefKey: string,
  defaultCols: readonly ColDef[],
  userId: string | null | undefined,
  migrate?: MigrateOptions,
  /** Default values for any extra fields stored alongside order/hidden (e.g. sortBy, sortDir). */
  defaultExtra?: Record<string, unknown>,
  /** Column ids hidden by default for a first-time user (default: none hidden, i.e. show all). */
  defaultHidden?: string[]
) {
  const defaultOrder = useMemo(() => defaultCols.map(c => c.id), [defaultCols])
  const [order, setOrderState]   = useState<string[]>(() => defaultCols.map(c => c.id))
  const [hidden, setHiddenState] = useState<string[]>(() => defaultHidden ?? [])
  const [extra, setExtraState]   = useState<Record<string, unknown>>(defaultExtra ?? {})
  const [loaded, setLoaded]      = useState(false)

  // Ref so save callbacks always see the current extra without stale closure issues.
  const extraRef = useRef(extra)
  useEffect(() => { extraRef.current = extra }, [extra])

  useEffect(() => {
    if (!userId) { setLoaded(true); return }
    supabase
      .from('user_preferences')
      .select('preferences')
      .eq('user_id', userId)
      .maybeSingle()
      .then(({ data, error }) => {
        if (error) console.error(`[useColumnPrefs] Failed to load prefs for "${prefKey}":`, error)
        const prefs = ((data?.preferences ?? {}) as Record<string, any>)
        let saved = prefs[prefKey]

        // One-time migration from old DB key
        if (!saved && migrate?.dbKey && prefs[migrate.dbKey]) {
          saved = prefs[migrate.dbKey]
          if (saved?.order || saved?.hidden) {
            mergeAndSavePrefs(userId, prefKey, saved.order || [], saved.hidden || [])
          }
        }

        // One-time migration from localStorage
        if (!saved && (migrate?.lsHidden || migrate?.lsOrder)) {
          try {
            const lsHidden = migrate.lsHidden
              ? (JSON.parse(localStorage.getItem(migrate.lsHidden) || '[]') as string[])
              : []
            const lsOrder = migrate.lsOrder
              ? (JSON.parse(localStorage.getItem(migrate.lsOrder) || '[]') as string[])
              : []
            if (lsHidden.length > 0 || lsOrder.length > 0) {
              saved = { hidden: lsHidden, order: lsOrder }
              mergeAndSavePrefs(userId, prefKey, lsOrder, lsHidden)
              if (migrate.lsHidden) localStorage.removeItem(migrate.lsHidden)
              if (migrate.lsOrder)  localStorage.removeItem(migrate.lsOrder)
            }
          } catch { /* ignore */ }
        }

        if (saved) {
          const validIds = new Set(defaultCols.map(c => c.id))
          const savedOrder: string[] = (saved.order as string[] || []).filter((id: string) => validIds.has(id))
          for (const col of defaultCols) {
            if (!savedOrder.includes(col.id)) savedOrder.push(col.id)
          }
          setOrderState(savedOrder)
          setHiddenState((saved.hidden as string[] || []).filter((id: string) => validIds.has(id)))
          // Restore extra fields (everything except order + hidden)
          const { order: _o, hidden: _h, ...rest } = saved
          const loadedExtra = { ...(defaultExtra ?? {}), ...rest }
          setExtraState(loadedExtra)
          extraRef.current = loadedExtra
        }
        setLoaded(true)
      })
  }, [userId, prefKey]) // eslint-disable-line react-hooks/exhaustive-deps

  const save = (o: string[], h: string[]) => {
    if (!userId) return
    mergeAndSavePrefs(userId, prefKey, o, h, extraRef.current)
  }

  const setOrder = (o: string[]) => { setOrderState(o); save(o, hidden) }
  const toggleHidden = (id: string) => {
    const h = hidden.includes(id) ? hidden.filter(x => x !== id) : [...hidden, id]
    setHiddenState(h); save(order, h)
  }
  const setExtra = (e: Record<string, unknown>) => {
    setExtraState(e)
    extraRef.current = e
    if (userId) mergeAndSavePrefs(userId, prefKey, order, hidden, e)
  }
  const reset = () => {
    const de = defaultExtra ?? {}
    const dh = defaultHidden ?? []
    setOrderState(defaultOrder)
    setHiddenState(dh)
    setExtraState(de)
    extraRef.current = de
    if (userId) mergeAndSavePrefs(userId, prefKey, defaultOrder, dh, de)
  }

  const visibleCols = useMemo<ColDef[]>(() => {
    const orderedIds = [...order.filter(id => defaultCols.some(c => c.id === id))]
    for (const col of defaultCols) {
      if (!orderedIds.includes(col.id)) orderedIds.push(col.id)
    }
    return orderedIds
      .filter(id => !hidden.includes(id))
      .map(id => defaultCols.find(c => c.id === id)!)
      .filter(Boolean)
  }, [order, hidden, defaultCols])

  return { order, hidden, extra, visibleCols, loaded, setOrder, toggleHidden, setExtra, reset }
}
